import { Injectable } from '@angular/core';
import { filter, map, Observable, pairwise, BehaviorSubject } from 'rxjs';
import { BooxMessageService } from './boox-message.service';
import { BooxStorageService } from './boox-storage.service';
import { BooxSessionService, ISessionModel } from './boox-session.service';


/**
 * Interface representing the application state.
 * Can hold any additional key-value pairs.
 */
export interface IAppState {
  status: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class BooxAppService {

  // Holds the latest application state
  protected state: IAppState;

  // BehaviorSubject ensures that new subscribers receive the latest value immediately
  protected _state$: BehaviorSubject<IAppState>;

  // Public observable for state changes (read-only)
  public readonly state$: Observable<IAppState>;

  constructor(
    protected message: BooxMessageService,
    protected storage: BooxStorageService,
    protected session: BooxSessionService,
  ) {
    this.state = this.storage.getItem('app.state', { status: 'init' });

    // Initialize the BehaviorSubject with the default state
    this._state$ = new BehaviorSubject<IAppState>(this.state);

    // Expose the state$ observable for listening to state changes
    this.state$ = this._state$.asObservable();
  }

  /**
   * Subscribe to changes of a specific key in the state.
   * Emits only when the value of the key changes.
   * 
   * @param key - The state key to listen for changes
   * @returns Observable emitting the new value when it changes
   */
  public key$(key: string): Observable<any> {
    return this.state$.pipe(
      pairwise(), // Get both the previous and next state
      filter(([prev, next]) => prev[key] !== next[key]), // Only emit if the value has changed
      map(([_, next]) => next[key]) // Return the new value of the key
    );
  }

  /**
   * Update a specific key in the application state.
   * Triggers an update in the BehaviorSubject.
   * 
   * @param key - The key to update
   * @param value - The new value to assign
   */
  public update(key: string, value: any): void {
    // Create a new state object with the updated key
    this.state = { ...this.state, [key]: value };
    // Save the updated state to local storage
    this.storage.setItem('app.state', this.state);
    // Emit the new state to all subscribers
    this._state$.next(this.state);
  }

  /**
   * Resets the entire application state, keeping only the status key.
   * Can be used for clearing the application state when needed.
   */
  public clear(): void { this.update('status', 'init'); }

  /**
   * Retrieves the current value of a specific key in the state.
   * 
   * @param key - The key whose value is needed
   * @returns The value of the specified key or undefined if not found
   */
  public get(key: string): any {
    return this.state[key];
  }

  /**
   * Retrieves a full copy of the current application state.
   * Useful when working with the entire state at once.
   * 
   * @returns A shallow copy of the state object
   */
  public getAll(): IAppState {
    return { ...this.state };
  }

  /**
   * Updates the application status and sends a corresponding message.
   * This method prevents duplicate logic for status updates.
   * 
   * @param status - The new application status
   */
  protected setStatus(status: string): void {
    this.update('status', status); // Update the state with the new status
  }

  // the following methods are used to update the application status

  /**
   * Marks the application as booting.
   * Typically called during startup.
   * 
   * order : 1
   * 
   */
  public boot() { this.setStatus('booting'); }

  /**
   * Marks the application as ready.
   * Indicates that all necessary initialization is complete.
   * 
   * order : 2
   * 
   */
  public ready() { this.setStatus('ready'); }

  /**
   * Marks the application as starting.
   * Used when transitioning from an idle state to an active state.
   * 
   * order : 3
   * 
   */
  public start() { this.setStatus('starting'); }

  /**
   * Marks the application as running.
   * Indicates that the application is in an active, operational state.
   * 
   * order : 4
   * 
   */
  public running() { this.setStatus('running'); }

  /**
   * Marks the application as stopping.
   * Indicates that the application is in the process of shutting down.
   * 
   * order : 5
   * 
   */
  public stop() { this.setStatus('stopping'); }

  /**
   * Marks the application as stopped.
   * Used when the application is fully shut down.
   * 
   * order : 6
   * 
   */
  public stopped() { this.setStatus('stopped'); }
}
